import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import AgencyTokenLogin from './modules/login/AgencyTokenLogin';
import PassengerTokenLogin from "./modules/login/PassengerTokenLogin";
import PassengerLoginForm from './modules/login/PassengerLoginForm';
import PassengerLogout from './modules/login/PassengerLogout';
import AgencyLogout from './modules/login/AgencyLogout';
import NotFoundModule from './modules/notfound/index';
import { getBackToCruisePlannerLink, getBackToDashboardText, getProjectName } from './utils/themeUtils';
import {Routes as RouterRoutes, Route, useNavigate, Navigate } from 'react-router';
import Dashboard from "./modules/overview/Dashboard";
import Itinerary from './modules/overview/Itinerary';
import ExcursionsList from './modules/excursions/ExcursionsList';
import ExcursionDetail from './modules/excursions/ExcursionDetail';
import PassengerDetails from './modules/checkout/PassengerDetails';
import RestaurantsList from './modules/tac/RestaurantsList';
import PaymentConfirmationB2C from './modules/checkout/PaymentConfirmationB2C';
import { updateBookings, updateCart } from './actions/cartActions';
import AuthRoute from "./components/routing/AuthRoute";
import GuestRoute from "./components/routing/GuestRoute";
import AnyAuthRoute from "./components/routing/AnyAuthRoute";
import PageErrorBoundary from "./components/routing/PageErrorBoundary";
import BackLink from "./components/BackLink";
import PaymentConfirmationB2B from "./modules/checkout/PaymentConfirmationB2B";
import AgencySSORedirect from "./modules/login/AgencySSORedirect";
import PrecruiseRoutes from "./modules/routes/PreCruiseRoutes";
import BoardingPass from "./modules/boarding/BoardingPass";
import BoardingPassDetail from "./modules/boarding/BoardingPassDetail";
import LuggageTag from "./modules/luggage/LuggageTag";
import LuggageTagDetail from "./modules/luggage/LuggageTagDetail";
import { getCookieConsentValue } from "react-cookie-consent";
import SessionIdLogin from "./modules/login/SessionIdLogin";
import { handleStorageEvent } from "./utils/miscUtils";
import { getExternalDashboardUrl, showExpiredTabMessage, useGoogleAnalytics } from "./utils/configUtils";
import MultipleTabsInUse from './components/MultipleTabsInUse';
import { t } from "@lingui/core/macro";
import { setNavigate } from './utils/navigationUtils';

function Routes(props) {

    // Extract navigate function, so it can be used all over the code
    const navigate = useNavigate();
    setNavigate(navigate);


    useEffect(() => {
        const loginPaths = RegExp('^/(token|login|agencyToken|passengerToken)');

        if (props.isAuthenticated === true && !loginPaths.test(window.location.pathname)) {
            updateCart();
            updateBookings()
        }

        const onPopState = () => {
            if (!props.isItineraryScrollSet) {
                window.scrollTo(0, 0);
            }

            // TODO - temporary change only - revert back once texts and wishes are known
            //const consentGiven = getCookieConsentValue();
            const consentGiven = "true";
            if (useGoogleAnalytics() && consentGiven === "true") {
                // TODO - bugfix for double precruise/passenger/:passengerId/form tracking due to handling back buttons on form steps
                // ReactGA.pageview(`${location.pathname}${location.search}`);
            }
        };

        window.addEventListener('popstate', onPopState);

        if (showExpiredTabMessage()) {
            window.addEventListener("storage", handleStorageEvent);
        }

        return () => {
            window.removeEventListener('popstate', onPopState);
            if (showExpiredTabMessage()) {
                window.removeEventListener("storage", handleStorageEvent);
            }
        };
    }, [props.isAuthenticated, props.isItineraryScrollSet]);

    // isExpiredTab will only ever be set if showExpiredTabMessage is true AND user logs in/out while multiple tabs with the app are open
    if (props.isExpiredTab) {
        return (
            <MultipleTabsInUse />
        )
    }

    // where each group of pages sends the guest when one of them crashes, see PageErrorBoundary
    // a trade portal session leaves for its booking on the trade portal, same as the Dashboard and Itinerary back links
    const externalDashboardUrl = getExternalDashboardUrl();
    const backToExternalDashboard = props.tradePortalBookingUrl ?
        <BackLink to={props.tradePortalBookingUrl} isExternal={true} text={getBackToDashboardText(true)} /> :
        externalDashboardUrl &&
        <BackLink to={externalDashboardUrl} isExternal={true} text={t({ id: "back_to_cruise_control_dashboard", message: `Back to ${getProjectName()} Dashboard` })} />;
    const backToDashboard = <BackLink to={t({ id: "/dashboard" })} text={getBackToDashboardText()} />;
    const backToPlanner = <BackLink to={t({ id: "/itinerary" })} text={t({ id: "back_to_excursions_list" })} />;

    return (
        <RouterRoutes>
            <Route path="/agent" element={<AgencySSORedirect />} />

            <Route path="/login" element={<GuestRoute component={PassengerLoginForm} />} />
            <Route path={t({ id: "/logoutUser" })} element={<PassengerLogout />} />
            <Route path="/logoutAgency/:forgetStore?" element={<AgencyLogout />} />

            <Route path="/token/:token" element={<AgencyTokenLogin />} />
            <Route path="/passengerToken/:token" element={<PassengerTokenLogin />} />
            <Route path="/sessionToken/:sessionId/:successUrl?" element={<SessionIdLogin />} />

            <Route element={<PageErrorBoundary backLink={backToExternalDashboard} />}>
                <Route path="/" element={<AuthRoute component={Dashboard} />} />
                <Route path={t({ id: "/dashboard" })} element={<AuthRoute component={Dashboard} />} />
                <Route path={t({ id: "/itinerary" })} element={<AuthRoute component={Itinerary} />} />
            </Route>

            <Route element={<PageErrorBoundary backLink={backToDashboard} />}>
                <Route path={t({ id: "/precruise" }) + "/*"} element={<PrecruiseRoutes />} />

                <Route path={t({ id: "/boarding" })} element={<AuthRoute component={BoardingPass} />} />
                <Route path={t({ id: "/luggage" })} element={<AuthRoute component={LuggageTag} />} />

                {/* public, so guests who arrive through an emailed hash link can open the print view */}
                <Route path={t({ id: "/boarding/detail" })} element={<BoardingPassDetail />} />
                <Route path={t({ id: "/luggage/detail" })} element={<LuggageTagDetail />} />

                <Route path="*" element={<AuthRoute component={NotFoundModule} />} />
            </Route>

            {/* emailed hash links for guests who aren't logged in, so there is no dashboard to go back to */}
            <Route element={<PageErrorBoundary />}>
                <Route path="/boardingPass/:hash" element={<BoardingPass />} />
                <Route path="/luggageTag/:hash" element={<LuggageTag />} />
            </Route>

            <Route element={<PageErrorBoundary backLink={backToPlanner} />}>
                <Route path={t({ id: "/excursions/:itineraryId" })} element={<AuthRoute component={ExcursionsList} />} />
                <Route path={t({ id: "/excursion/:itineraryId/:excursionId/:modal?" })} element={<AuthRoute component={ExcursionDetail} />} />

                <Route path={t({ id: "/tac/restaurants" }) + "/:date/:type?"} element={<AuthRoute component={RestaurantsList} />} />
                {/* the date used to be passed in the location state, so old links carry no date */}
                <Route path={t({ id: "/tac/restaurants" })} element={<Navigate to={getBackToCruisePlannerLink()} replace />} />

                <Route path={t({ id: "/checkout" })} element={<AuthRoute component={PassengerDetails} />} />
                <Route path={t({ id: "/confirmation/b2b" })} element={<AnyAuthRoute component={PaymentConfirmationB2B} />} />
                <Route path="/destinations/book/confirmation" element={<AuthRoute component={PaymentConfirmationB2C} />} />
            </Route>
        </RouterRoutes>
    );
}

function mapStateToProps(state) {
    return {
        isAuthenticated: state.authentication.isAuthenticated,
        tradePortalBookingUrl: state.authentication.tradeportal?.bookingUrl,
        isItineraryScrollSet: (state.itinerary.findIndex((day) => day.scrollTo === true) > -1),
        isExpiredTab: state.location.isExpiredTab,
    };
}

export default connect(mapStateToProps)(Routes);